import { Message } from "@/components/Message/Message";
import {
  getMessagesAction,
  isLoadingMessagesAtom,
  messagesAtom,
  pendingMessagesAtom,
  replyedMessageAtom,
  sendMessagesAction,
} from "@/core/reatom/state/messages/messages";
import { currentOrderAtom } from "@/core/reatom/state/orders/orders";
import { useAction, useAtom } from "@reatom/npm-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import s from "./style.module.scss";
import dayjs from "dayjs";
import { Reply } from "@/components/Reply/Reply";
import { MessageItem } from "@/core/reatom/state/messages/types";
import Send from "@/assets/send.svg?react";
import { ResizableTextArea } from "@/components/ResizableTextarea/ResizableTextarea";
import { SpinLoader } from "@/components/SpinLoader/SpinLoader";
import clsx from "clsx";
import { Icon28AddCircleOutline } from "@vkontakte/icons";

import {
  deleteFileToSessionAction,
  uploadFileToSessionAction,
  uploadFileToSessionAtom,
  uploadSessionAction,
  uploadSessionAtom,
} from "@/core/reatom/state/upload/upload";
import { MessageFiles } from "@/components/MessageFiles/MessageFiles";

export const MessagesPage = () => {
  const { id } = useParams();
  const [currentOrder] = useAtom(currentOrderAtom);
  const handleFetchMessages = useAction(getMessagesAction);
  const sendMessage = useAction(sendMessagesAction);
  const [messages] = useAtom(messagesAtom);
  const [pendingMessages] = useAtom(pendingMessagesAtom);
  const [isLoading] = useAtom(isLoadingMessagesAtom);
  const [_, setOffsetLength] = useState(1);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const [hasScrolledInitially, setHasScrolledInitially] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollCompensation, setScrollCompensation] = useState<null | {
    prevScrollTop: number;
    prevScrollHeight: number;
  }>(null);
  useEffect(() => {
    if (!messages?.has_next_page) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (
          firstEntry.isIntersecting &&
          !isLoading &&
          messages?.has_next_page
        ) {
          const container = containerRef.current;
          const prevScrollHeight = container?.scrollHeight ?? 0;
          const prevScrollTop = container?.scrollTop ?? 0;

          setOffsetLength((prev) => {
            setScrollCompensation({ prevScrollHeight, prevScrollTop });
            handleFetchMessages(Number(id), prev * 10, true);
            return prev + 1;
          });
        }
      },
      { root: null, rootMargin: "0px", threshold: 1.0 }
    );

    const sentinel = topSentinelRef.current;
    if (sentinel) observer.observe(sentinel);

    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [messages?.has_next_page, isLoading]);
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    return () => window.removeEventListener("resize", setVH);
  }, []);

  useEffect(() => {
    handleFetchMessages(id ? +id : 0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      handleFetchMessages(id ? +id : 0, 0, true, true);
    }, 20000);

    return () => {
      clearInterval(timer);
    };
  }, [currentOrder]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView();
  };
  useEffect(() => {
    if (!hasScrolledInitially && messages?.items.length) {
      scrollToBottom();
      setHasScrolledInitially(true);
    }
  }, [messages, hasScrolledInitially]);

  useEffect(() => {
    if (!scrollCompensation || !containerRef.current) return;

    requestAnimationFrame(() => {
      const container = containerRef.current!;
      const newScrollHeight = container.scrollHeight;

      container.scrollTop =
        scrollCompensation.prevScrollTop +
        (newScrollHeight - scrollCompensation.prevScrollHeight);

      setScrollCompensation(null); // сбрасываем
    });
  }, [messages?.items]);
  const [value, setValue] = useState("");
  const reversedMessages = useMemo(
    () => messages?.items.slice().reverse() ?? [],
    [messages]
  );
  const [replyed, setReplyed] = useAtom(replyedMessageAtom);
  const replyName =
    replyed?.from_id == messages?.orders[0].author.id
      ? messages?.orders[0].author.full_name ??
        messages?.orders[0].author.username
      : messages?.orders[0].arbitrators.find((el) => el.id === replyed?.from_id)
      ? messages?.orders[0].arbitrators.find((el) => el.id === replyed?.from_id)
          ?.first_name
      : messages?.orders[0].freelancers[0].full_name;
  const handleFocus = (e) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  };

  const handleCreateUploadFile = useAction(uploadSessionAction);
  const [uploadSession] = useAtom(uploadSessionAtom);

  const handleUploadFiles = useAction(uploadFileToSessionAction);

  const [uploadedFiles] = useAtom(uploadFileToSessionAtom);

  const handleDelete = useAction(deleteFileToSessionAction);

  useEffect(() => {
    if (!uploadSession?.session_id) {
      handleCreateUploadFile(`${id ? +id : 0}`);
    }
  }, [uploadSession]);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current += 1;
    const hasFiles = Array.from(e.dataTransfer?.items || []).some(
      (item) => item.kind === "file"
    );
    if (hasFiles) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;

    if (dragCounter.current <= 0) {
      setIsDragging(false);
      dragCounter.current = 0;
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;

    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;

    if (uploadSession?.session_id && droppedFiles.length > 0) {
      handleUploadFiles({
        session_id: uploadSession.session_id,
        files: droppedFiles,
      });
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;

    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0 && uploadSession?.session_id) {
      handleUploadFiles({
        session_id: uploadSession.session_id,
        files,
      });
    }
  };

  return (
    <div
      className={s.wrapper}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
    >
      <div className={s.header}>
        {!isLoading && (
          <>
            <img
              src={messages?.orders[0].freelancers[0].photo_url}
              alt=""
              className={s.avatar}
            />
            <p className={s.title}>
              {messages?.orders[0].freelancers[0].full_name ??
                messages?.orders[0].freelancers[0].username}
            </p>
          </>
        )}
      </div>
      {isLoading ? (
        <SpinLoader />
      ) : (
        <>
          <div className={s.messages} ref={containerRef}>
            {messages?.has_next_page && (
              <div ref={topSentinelRef}>
                <SpinLoader />
              </div>
            )}
            {[...reversedMessages, ...pendingMessages].map((item, index) => {
              const prevMessage = reversedMessages[index - 1];
              const showDate =
                !prevMessage ||
                !dayjs(item.time).isSame(prevMessage.time, "day");
              const isPending = "isPending" in item && item.isPending;
              return (
                <Fragment key={item.id}>
                  {showDate && (
                    <div className={s.date}>
                      {dayjs(item.time).format("D MMMM")}
                    </div>
                  )}
                  <Message
                    files={isPending ? undefined : (item as MessageItem).files}
                    id={item.id}
                    senderId={
                      isPending ? undefined : (item as MessageItem).from_id
                    }
                    textType={isPending ? "raw" : (item as MessageItem).format}
                    message={item.text ?? ""}
                    timestamp={item.time}
                    replyTo={item.reply_to ?? null}
                    setReplyed={() =>
                      isPending ? null : setReplyed(item as MessageItem)
                    }
                    isPending={isPending}
                  />
                </Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          <div className={clsx(s.bottom)}>
            {isDragging && (
              <div className={s.dragOverlay}>
                <div className={s.dragContent}>Перетащите файлы сюда</div>
              </div>
            )}
            {replyed && (
              <div className={s.replyWrapper}>
                <Reply
                  replyName={replyName}
                  replyTextShort={replyed.text}
                  files={replyed.files}
                  cancelReply={() => setReplyed(null)}
                />
              </div>
            )}
            {uploadedFiles && uploadedFiles.items.length > 0 && (
              <div className={s.uploadedFiles}>
                {uploadedFiles.items.map((file) => {
                  if (file.name.startsWith("sm_")) return null;

                  return (
                    <MessageFiles
                      file={file}
                      key={file.id}
                      isReply
                      onDelete={() => {
                        handleDelete({
                          session_id: uploadSession?.session_id ?? "",
                          files: file,
                        });
                      }}
                    />
                  );
                })}
              </div>
            )}

            <div className={clsx(s.inputWrapper)}>
              <input
                type="file"
                id="file-upload"
                style={{ display: "none" }}
                onChange={(e) => {
                  const files = e.target.files;
                  if (uploadSession?.session_id && e.target.files) {
                    handleUploadFiles({
                      session_id: uploadSession?.session_id,
                      files: e.target.files,
                    });
                  }
                }}
                multiple
              />
              <label htmlFor="file-upload">
                <Icon28AddCircleOutline className={s.icon} />
              </label>
              <ResizableTextArea
                value={value}
                onFocus={(e) => handleFocus(e)}
                // className={s.input}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Сообщение"
                onPaste={handlePaste}
              />

              <button
                onClick={() => {
                  if (
                    !value.trim() &&
                    !(uploadedFiles && uploadedFiles?.items.length > 0)
                  )
                    return;
                  sendMessage({
                    id: Number(id),
                    ...(value && { text: value }),
                    reply_to: replyed?.id,
                    file_session_id: uploadSession?.session_id ?? "",
                  }).then(() => {});
                  setTimeout(() => scrollToBottom(), 1);
                  setValue("");
                  setReplyed(null);
                }}
                className={s.button}
              >
                <Send className={s.send} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
